import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { DEFAULT_CONNECT } from "../lib/homeContent";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const initial = { name: "", email: "", company: "", subject: "", message: "" };

export default function Connect() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [c, setC] = useState(DEFAULT_CONNECT);

  useEffect(() => {
    api.get("/content/connect").then(({ data }) => setC({ ...DEFAULT_CONNECT, ...data })).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="connect-page" className="bg-sp-bg">
      <section className="pt-40 pb-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left column */}
          <div className="lg:col-span-5">
            <p className="sp-eyebrow">{c.eyebrow}</p>
            <h1 className="mt-6 font-serif text-4xl md:text-6xl text-sp-deep leading-[1.08]">
              {c.title}
            </h1>
            <p className="mt-6 font-sans text-lg leading-relaxed text-sp-soft max-w-md">
              {c.subtitle}
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`mailto:${c.email}`}
                data-testid="connect-email"
                className="flex items-start gap-4 group"
              >
                <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-sp-navy/20 text-sp-navy group-hover:bg-sp-navy group-hover:text-white transition-colors duration-300">
                  <Mail className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-sp-soft">Email</p>
                  <p className="font-sans text-base text-sp-deep">{c.email}</p>
                </span>
              </a>
              <div className="flex items-start gap-4">
                <span className="mt-1 flex h-10 w-10 items-center justify-center rounded-full border border-sp-navy/20 text-sp-navy">
                  <MapPin className="w-4 h-4" strokeWidth={1.75} />
                </span>
                <span>
                  <p className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-sp-soft">Online</p>
                  <p className="font-sans text-base text-sp-deep">{c.online}</p>
                </span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <motion.form
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              onSubmit={submit}
              data-testid="connect-form"
              className="bg-white border border-sp-navy/10 rounded-sm p-8 md:p-10 shadow-[0_4px_24px_rgba(40,78,120,0.06)]"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-sp-deep">Full name *</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={update("name")} className="mt-2" placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-sp-deep">Email *</Label>
                  <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={update("email")} className="mt-2" placeholder="you@company.com" />
                </div>
              </div>
              <div className="mt-6">
                <Label htmlFor="company" className="text-sp-deep">Company / Fund</Label>
                <Input id="company" data-testid="contact-company" value={form.company} onChange={update("company")} className="mt-2" placeholder="Organisation" />
              </div>
              <div className="mt-6">
                <Label htmlFor="subject" className="text-sp-deep">Subject *</Label>
                <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={update("subject")} className="mt-2" placeholder="How can we help?" />
              </div>
              <div className="mt-6">
                <Label htmlFor="message" className="text-sp-deep">Message *</Label>
                <Textarea id="message" data-testid="contact-message" value={form.message} onChange={update("message")} rows={5} className="mt-2" placeholder="Tell us about your project or mandate…" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-8 group inline-flex items-center gap-2 rounded-full bg-sp-navy px-8 py-3.5 text-sm font-medium text-white hover:bg-sp-deep transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Message"}
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
              </button>
            </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
}
